import { useCallback, useEffect, useState } from 'react';
import {
  Button, Menu, MenuDivider, MenuGroup, MenuGroupHeader, MenuItem, MenuList, MenuPopover, MenuTrigger,
} from '@fluentui/react-components';
import { ChevronDown12Regular } from '@fluentui/react-icons';
import type { FilterColumn } from './types';
import type { GroupGranularity, ViewGroup, ViewSort } from './viewDefinition';
import { GROUP_GRANULARITIES } from './viewDefinition';
import { isGroupable } from './grouping';
import i18n from '../../i18n';
import { acquireFilterSurface, releaseFilterSurface } from './filterSurfaceRegistry';

/**
 * The column header menu — fleet filter contract v2 §13.1 RULE M1:
 *
 * | item | where its state lives |
 * |---|---|
 * | Sort A → Z / Z → A | URL (`sort`, `dir`) |
 * | Group by (dates: Year · Quarter · Month · Week · Day) | URL (`group`, `groupBy`) |
 * | Filter by… | URL, on Apply — opens the column's staging popover (§6.6 I5) |
 * | Column width | per-user preference — never the URL (M2) |
 * | Move left / Move right | per-user preference — never the URL (M2) |
 *
 * An item the column or table does not support is OMITTED, never shown disabled (C58): a disabled
 * item with no explanation reads as a bug; an absent one reads as a table that does not do that.
 *
 * It is a floating surface, so it joins the one-surface registry (§6.6 I1): opening it closes any open
 * filter popover, and opening a popover closes it. Fluent's Menu owns its own outside-click and Escape
 * dismissal, and nothing here is staged, so there is nothing for a dismissal to discard.
 */

const granularityLabel = (g: GroupGranularity) => i18n.t(`filters.granularity.${g}`);

export const WIDTH_STEP = 40;

export interface ColumnHeaderMenuProps<T> {
  column: FilterColumn<T>;
  /** The header's own arrow shows the active sort; the menu only sets it. */
  onSortChange: (next: ViewSort | null) => void;
  /** Omit to declare the table does not group (C66). */
  group?: ViewGroup | null;
  onGroupChange?: (next: ViewGroup | null) => void;
  /** Opens this column's filter popover. Omit when the column is not filterable. */
  onFilterRequest?: () => void;
  width?: number;
  defaultWidth: number;
  onWidthChange?: (px: number | null) => void;
  canMoveLeft?: boolean;
  canMoveRight?: boolean;
  onMoveLeft?: () => void;
  onMoveRight?: () => void;
}

export function ColumnHeaderMenu<T>({
  column, onSortChange, group, onGroupChange, onFilterRequest,
  width, defaultWidth, onWidthChange, canMoveLeft, canMoveRight, onMoveLeft, onMoveRight,
}: ColumnHeaderMenuProps<T>) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  useEffect(() => () => releaseFilterSurface(close), [close]);

  const onOpenChange = (_: unknown, data: { open: boolean }) => {
    setOpen(data.open);
    if (data.open) acquireFilterSurface(close);
    else releaseFilterSurface(close);
  };

  const sortable = !!column.sortKey;
  const groupable = !!onGroupChange && isGroupable(column);
  const isDate = column.filterType === 'date-range';
  const groupedHere = group?.col === column.key;
  const current = width ?? defaultWidth;
  const run = (fn: () => void) => () => {
    fn();
    setOpen(false);
    releaseFilterSurface(close);
  };

  return (
    <Menu open={open} onOpenChange={onOpenChange}>
      <MenuTrigger disableButtonEnhancement>
        <Button
          appearance="subtle"
          size="small"
          icon={<ChevronDown12Regular />}
          aria-label={i18n.t('filters.columnOptions', { column: column.label })}
        />
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {sortable ? (
            <>
              <MenuItem onClick={run(() => onSortChange({ col: column.sortKey!, dir: 'asc' }))}>{i18n.t('filters.sortAsc')}</MenuItem>
              <MenuItem onClick={run(() => onSortChange({ col: column.sortKey!, dir: 'desc' }))}>{i18n.t('filters.sortDesc')}</MenuItem>
            </>
          ) : null}

          {groupable ? (
            <>
              <MenuDivider />
              <MenuGroup>
                <MenuGroupHeader>{i18n.t('filters.groupBy')}</MenuGroupHeader>
                {isDate ? (
                  GROUP_GRANULARITIES.map((g) => (
                    <MenuItem key={g} onClick={run(() => onGroupChange!({ col: column.key, granularity: g }))}>
                      {granularityLabel(g)}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem onClick={run(() => onGroupChange!({ col: column.key, granularity: null }))}>
                    {column.label}
                  </MenuItem>
                )}
                {groupedHere ? <MenuItem onClick={run(() => onGroupChange!(null))}>{i18n.t('filters.removeGrouping')}</MenuItem> : null}
              </MenuGroup>
            </>
          ) : null}

          {onFilterRequest ? (
            <>
              <MenuDivider />
              <MenuItem onClick={run(onFilterRequest)}>{i18n.t('filters.filterBy')}</MenuItem>
            </>
          ) : null}

          {onWidthChange ? (
            <>
              <MenuDivider />
              <MenuGroup>
                <MenuGroupHeader>{i18n.t('filters.columnWidth')}</MenuGroupHeader>
                <MenuItem onClick={run(() => onWidthChange(current + WIDTH_STEP))}>{i18n.t('filters.wider')}</MenuItem>
                <MenuItem onClick={run(() => onWidthChange(current - WIDTH_STEP))}>{i18n.t('filters.narrower')}</MenuItem>
                {width !== undefined ? <MenuItem onClick={run(() => onWidthChange(null))}>{i18n.t('filters.defaultWidth')}</MenuItem> : null}
              </MenuGroup>
            </>
          ) : null}

          {(canMoveLeft && onMoveLeft) || (canMoveRight && onMoveRight) ? <MenuDivider /> : null}
          {canMoveLeft && onMoveLeft ? <MenuItem onClick={run(onMoveLeft)}>{i18n.t('filters.moveLeft')}</MenuItem> : null}
          {canMoveRight && onMoveRight ? <MenuItem onClick={run(onMoveRight)}>{i18n.t('filters.moveRight')}</MenuItem> : null}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}
